/**
 * Ventana «Pendientes» del inicio: los pendientes agendados sobre cualquier
 * registro del ERP, en un solo lugar.
 *
 * Es el otro extremo del hilo de seguimiento (`views/ChatterPanel`). Ahi se
 * agenda un pendiente sobre un registro concreto —«llamar al proveedor de la OC
 * 42»— y aca aparece todo lo agendado, de los trece modulos a la vez, ordenado
 * por lo que vence primero. Sin esta pantalla, agendar seria escribirse un
 * papelito y meterlo en un cajon distinto por cada factura.
 *
 * Los datos salen de `GET /api/v1/activities/`, la unica consulta del chatter
 * que no parte de un registro. Filtra el SERVIDOR: un pendiente cuyo registro el
 * usuario no puede leer no viaja, aunque este a su nombre. Aca no se replica
 * ninguna regla de permisos, y es a proposito: la lista que se dibuja es la que
 * el servidor dejo pasar.
 *
 * `scope=mine` por defecto —el inicio es «lo tuyo de hoy», no la bandeja de la
 * oficina— con un interruptor para ver el de todos.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDate, formatRelativeDate } from '@/components/fields/format';
import { IconCheck, IconChevronRight, IconSpinner } from '@/components/shell/icons';
import { recordPath } from '@/components/shell/menu-utils';
import { Badge, Button, Card } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { ApiError, api } from '@/lib/api';
import type { ActivityBoard, ChatterActivity } from '@/lib/types';
import { DATA_KEYS, useCompanyId } from '@/views/queries';

/** Cuantos pendientes se piden. Es la lista para trabajar hoy, no el backlog. */
const TOPE = 25;

function mensajeDe(error: unknown): string | undefined {
  return error instanceof ApiError ? error.message : undefined;
}

/** Como se ve el vencimiento de un pendiente. */
function vencimiento(actividad: ChatterActivity): string {
  if (actividad.due_date === null) return 'Sin fecha';
  return `${formatDate(actividad.due_date)} · ${formatRelativeDate(actividad.due_date)}`;
}

export function PendientesPanel() {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const [alcance, setAlcance] = useState<'mine' | 'all'>('mine');
  const [enCurso, setEnCurso] = useState<number | null>(null);
  // Si el usuario ya tocó el interruptor de alcance, la sección se queda aunque
  // la lista quede vacía: esconderla ahí le sacaría de la pantalla el control
  // que acaba de usar y no habría forma de volver.
  const [tocado, setTocado] = useState(false);

  const clave = ['pendientes', companyId, alcance] as const;
  const consulta = useQuery({
    queryKey: clave,
    queryFn: ({ signal }) =>
      api.fetchActivities({ scope: alcance, state: 'planned', limit: TOPE }, signal),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const marcar = useMutation({
    mutationFn: (actividad: ChatterActivity) => {
      // El endpoint pide el registro del que cuelga: el permiso vuelve a ser el
      // del registro y no el del id del pendiente.
      const registro = actividad.record;
      if (registro === undefined) {
        return Promise.reject(new Error('El pendiente no dice de qué registro cuelga.'));
      }
      return api.setActivityState(registro.model, registro.id, actividad.id, 'done');
    },
    onSuccess: (_resultado, actividad) => {
      setEnCurso(null);
      notify.success('Pendiente marcado como hecho.');
      void queryClient.invalidateQueries({ queryKey: ['pendientes'] });
      // Y el hilo del registro del que cuelga: marcarlo hecho deja una traza.
      const registro = actividad.record;
      if (registro !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: DATA_KEYS.chatter(companyId, registro.model, registro.id),
        });
      }
    },
    onError: (error: unknown) => {
      setEnCurso(null);
      notify.error('No se pudo marcar el pendiente.', mensajeDe(error));
    },
  });

  // Sin permiso de lectura sobre ningun modelo con hilo el endpoint contesta
  // igual (lista vacia), asi que un error aca es de verdad: no se dibuja la
  // seccion antes que un cartel rojo en el inicio.
  if (consulta.isError) return null;

  const datos: ActivityBoard | undefined = consulta.data;
  const pendientes = datos?.activities ?? [];

  // Si no hay nada agendado el inicio no gana nada con un recuadro vacio: la
  // seccion aparece cuando hay trabajo.
  if (!consulta.isPending && pendientes.length === 0 && !tocado) return null;

  return (
    <section aria-labelledby="pendientes-titulo" className="mt-6">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {/* «Pendientes agendados» y no «Pendientes» a secas: en el mismo inicio,
            `TrabajoHoy` ya rotula «Pendientes» a los contadores (por cobrar,
            por liquidar). Aquéllos son un número que sale de los datos; éstos
            son cosas que una persona se anotó. Dos títulos iguales en la misma
            pantalla los haría pasar por lo mismo. */}
        <h2 id="pendientes-titulo" className="text-sm font-semibold uppercase tracking-wide text-fg-faint">
          Pendientes agendados
        </h2>
        {(datos?.overdue ?? 0) > 0 && <Badge tone="danger">{datos?.overdue} vencidos</Badge>}
        {(datos?.today ?? 0) > 0 && <Badge tone="warning">{datos?.today} para hoy</Badge>}
        <div className="ml-auto flex items-center gap-1">
          {(['mine', 'all'] as const).map((opcion) => (
            <Button
              key={opcion}
              size="sm"
              variant={alcance === opcion ? 'secondary' : 'ghost'}
              aria-pressed={alcance === opcion}
              onClick={() => {
                setTocado(true);
                setAlcance(opcion);
              }}
            >
              {opcion === 'mine' ? 'Míos' : 'De todos'}
            </Button>
          ))}
        </div>
      </div>

      <Card className="divide-y divide-line">
        {consulta.isPending ? (
          <p className="flex items-center gap-2 px-4 py-4 text-sm text-fg-muted">
            <IconSpinner className="h-4 w-4" /> Cargando pendientes…
          </p>
        ) : pendientes.length === 0 ? (
          <p className="px-4 py-4 text-sm text-fg-muted">
            No hay pendientes agendados. Se agendan desde el panel «Seguimiento» de cualquier ficha.
          </p>
        ) : (
          pendientes.map((actividad) => {
            const registro = actividad.record;
            return (
              <div key={actividad.id} className="flex items-start gap-3 px-4 py-3">
                <span
                  aria-hidden="true"
                  className={clsx(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    actividad.overdue ? 'bg-danger' : 'bg-accent',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{actividad.summary}</p>
                  <p className="truncate text-xs text-fg-muted">
                    {vencimiento(actividad)}
                    {actividad.assigned_to !== null && ` · ${actividad.assigned_to.display_name}`}
                  </p>
                  {registro !== undefined && (
                    <Link
                      to={recordPath(registro.model, registro.id)}
                      className="mt-0.5 inline-flex items-center gap-1 text-xs text-accent hover:underline"
                    >
                      {registro.model_label}: {registro.display_name}
                      <IconChevronRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
                {actividad.overdue && <Badge tone="danger">Vencido</Badge>}
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Marcar «${actividad.summary}» como hecho`}
                  loading={enCurso === actividad.id}
                  onClick={() => {
                    setEnCurso(actividad.id);
                    marcar.mutate(actividad);
                  }}
                >
                  <IconCheck className="h-4 w-4" />
                  <span className="hidden sm:inline">Hecho</span>
                </Button>
              </div>
            );
          })
        )}
      </Card>
    </section>
  );
}
